import React, { useState, useEffect } from 'react';
import { FaTimes, FaWallet, FaBitcoin, FaDollarSign, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { branding } from '../constants/branding';
import { apiService } from '../services/apiService';

interface TradingAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (walletData: any) => void;
  userId: number;
}

interface CryptoWallet {
  id: number;
  address: string;
  currency: string;
  balance: number;
}

const TradingAccountModal: React.FC<TradingAccountModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userId
}) => {
  const [step, setStep] = useState<'funding' | 'confirmation' | 'success'>('funding');
  const [fundingSource, setFundingSource] = useState<'FIAT' | 'CRYPTO'>('FIAT');
  const [currency, setCurrency] = useState('USD');
  const [amount, setAmount] = useState('');
  const [cryptoWallets, setCryptoWallets] = useState<CryptoWallet[]>([]);
  const [selectedCryptoWallet, setSelectedCryptoWallet] = useState<number | null>(null);
  const [cryptoToken, setCryptoToken] = useState('');
  const [cryptoAmount, setCryptoAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('funding');
      setFundingSource('FIAT');
      setCurrency('USD');
      setAmount('');
      setSelectedCryptoWallet(null);
      setCryptoToken('');
      setCryptoAmount('');
      setNotes('');
      setError(null);
      setConsentChecked(false);
      fetchCryptoWallets();
    }
  }, [isOpen]);

  const fetchCryptoWallets = async () => {
    try {
      const response = await apiService.get(`/api/retail/crypto-trading/wallets/${userId}`);
      if (response.success && response.data) {
        setCryptoWallets(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch crypto wallets:', err);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const requestData: any = {
        currency: currency,
        initialAmount: parseFloat(amount),
        fundingSource: fundingSource,
        notes: notes
      };

      if (fundingSource === 'CRYPTO') {
        requestData.cryptoWalletId = selectedCryptoWallet;
        requestData.cryptoToken = cryptoToken;
        requestData.cryptoAmount = parseFloat(cryptoAmount);
      }

      const response = await apiService.post(`/api/retail/trading/wallets/${userId}`, requestData);

      if (response.success) {
        setStep('success');
        setTimeout(() => {
          onSuccess(response.data);
          onClose();
        }, 2000);
      } else {
        setError(response.error || 'Failed to create trading account');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create trading account');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getMinAmount = () => {
    return fundingSource === 'FIAT' ? 50 : 0.001;
  };

  const getMaxAmount = () => {
    return fundingSource === 'FIAT' ? 10000 : 1000;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 32,
        maxWidth: 500,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: `2px solid ${branding.primaryColor}`
        }}>
          <h2 style={{
            margin: 0,
            color: branding.textColor,
            fontSize: 24,
            fontWeight: 600
          }}>
            Create Trading Account
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              color: '#666',
              cursor: 'pointer',
              padding: 8,
              borderRadius: 4
            }}
          >
            <FaTimes />
          </button>
        </div>

        {step === 'funding' && (
          <>
            {/* Funding Source Selection */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block',
                marginBottom: 12,
                fontWeight: 600,
                color: branding.textColor
              }}>
                Funding Source
              </label>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => setFundingSource('FIAT')}
                  style={{
                    flex: 1,
                    padding: 16,
                    border: `2px solid ${fundingSource === 'FIAT' ? branding.primaryColor : '#e0e0e0'}`,
                    borderRadius: 8,
                    background: fundingSource === 'FIAT' ? `${branding.primaryColor}10` : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <FaDollarSign style={{ color: fundingSource === 'FIAT' ? branding.primaryColor : '#666' }} />
                  <span style={{ color: fundingSource === 'FIAT' ? branding.primaryColor : '#666' }}>
                    Fiat Currency
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setFundingSource('CRYPTO')}
                  style={{
                    flex: 1,
                    padding: 16,
                    border: `2px solid ${fundingSource === 'CRYPTO' ? branding.primaryColor : '#e0e0e0'}`,
                    borderRadius: 8,
                    background: fundingSource === 'CRYPTO' ? `${branding.primaryColor}10` : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <FaBitcoin style={{ color: fundingSource === 'CRYPTO' ? branding.primaryColor : '#666' }} />
                  <span style={{ color: fundingSource === 'CRYPTO' ? branding.primaryColor : '#666' }}>
                    Cryptocurrency
                  </span>
                </button>
              </div>
            </div>

            {/* Currency Selection */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 600,
                color: branding.textColor
              }}>
                Currency
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '2px solid #e0e0e0',
                  borderRadius: 8,
                  fontSize: 16,
                  backgroundColor: 'white'
                }}
              >
                {fundingSource === 'FIAT' ? (
                  <>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                  </>
                ) : (
                  <>
                    <option value="USDT">USDT - Tether</option>
                    <option value="USDC">USDC - USD Coin</option>
                    <option value="BTC">BTC - Bitcoin</option>
                    <option value="ETH">ETH - Ethereum</option>
                  </>
                )}
              </select>
            </div>

            {/* Amount Input */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 600,
                color: branding.textColor
              }}>
                Initial Amount
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    // If empty, set to empty string
                    if (rawValue === '' || rawValue === null || rawValue === undefined) {
                      setAmount('');
                      return;
                    }
                    // Remove leading zeros except for "0" itself or decimal numbers starting with "0."
                    let cleanedValue = rawValue;
                    if (rawValue !== '0' && !rawValue.startsWith('0.')) {
                      cleanedValue = rawValue.replace(/^0+(?=\d)/, '') || rawValue;
                    }
                    // Parse to validate it's a number
                    const numValue = parseFloat(cleanedValue);
                    // Only update if it's a valid number and non-negative
                    if (!isNaN(numValue) && numValue >= 0) {
                      setAmount(cleanedValue);
                    } else if (rawValue === '') {
                      setAmount('');
                    }
                  }}
                  placeholder={`Enter amount (min: ${getMinAmount()}, max: ${getMaxAmount()})`}
                  min={getMinAmount()}
                  max={getMaxAmount()}
                  step={fundingSource === 'FIAT' ? '0.01' : '0.000001'}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '2px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 16,
                    paddingLeft: 40
                  }}
                />
                <div style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666'
                }}>
                  {fundingSource === 'FIAT' ? <FaDollarSign /> : <FaBitcoin />}
                </div>
              </div>
              <div style={{
                fontSize: 12,
                color: '#666',
                marginTop: 4
              }}>
                Minimum: {formatAmount(getMinAmount(), currency)} | Maximum: {formatAmount(getMaxAmount(), currency)}
              </div>
            </div>

            {/* Crypto Wallet Selection (if crypto funding) */}
            {fundingSource === 'CRYPTO' && (
              <>
                <div style={{ marginBottom: 24 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 600,
                    color: branding.textColor
                  }}>
                    Select Crypto Wallet
                  </label>
                  <select
                    value={selectedCryptoWallet || ''}
                    onChange={(e) => setSelectedCryptoWallet(Number(e.target.value))}
                    style={{
                      width: '100%',
                      padding: 12,
                      border: '2px solid #e0e0e0',
                      borderRadius: 8,
                      fontSize: 16,
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select a wallet</option>
                    {cryptoWallets.map(wallet => (
                      <option key={wallet.id} value={wallet.id}>
                        {wallet.currency} - {wallet.address.substring(0, 8)}... - Balance: {wallet.balance}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 8,
                    fontWeight: 600,
                    color: branding.textColor
                  }}>
                    Crypto Amount
                  </label>
                  <input
                    type="number"
                    value={cryptoAmount}
                    onChange={(e) => setCryptoAmount(e.target.value)}
                    placeholder="Enter crypto amount"
                    step="0.000001"
                    style={{
                      width: '100%',
                      padding: 12,
                      border: '2px solid #e0e0e0',
                      borderRadius: 8,
                      fontSize: 16
                    }}
                  />
                </div>
              </>
            )}

            {/* Notes */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 600,
                color: branding.textColor
              }}>
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this funding..."
                rows={3}
                style={{
                  width: '100%',
                  padding: 12,
                  border: '2px solid #e0e0e0',
                  borderRadius: 8,
                  fontSize: 16,
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Consent Checkbox */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  style={{
                    marginTop: 2,
                    transform: 'scale(1.2)'
                  }}
                />
                <span style={{ fontSize: 14, color: branding.textColor }}>
                  I acknowledge that trading involves risk and I have read and agree to the trading terms and conditions.
                </span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                padding: 12,
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: 8,
                marginBottom: 24,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <FaExclamationTriangle style={{ color: '#c33' }} />
                <span style={{ color: '#c33' }}>{error}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e0e0e0',
                  borderRadius: 8,
                  background: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: 16,
                  fontWeight: 600
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !amount || !consentChecked || (fundingSource === 'CRYPTO' && !selectedCryptoWallet)}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: 8,
                  background: loading || !amount || !consentChecked || (fundingSource === 'CRYPTO' && !selectedCryptoWallet) 
                    ? '#ccc' 
                    : branding.primaryColor,
                  color: 'white',
                  cursor: loading || !amount || !consentChecked || (fundingSource === 'CRYPTO' && !selectedCryptoWallet) 
                    ? 'not-allowed' 
                    : 'pointer',
                  fontSize: 16,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                {loading ? 'Creating...' : 'Create Trading Account'}
                <FaWallet />
              </button>
            </div>
          </>
        )}

        {step === 'success' && (
          <div style={{
            textAlign: 'center',
            padding: 32
          }}>
            <FaCheckCircle style={{
              fontSize: 64,
              color: branding.primaryColor,
              marginBottom: 24
            }} />
            <h3 style={{
              color: branding.textColor,
              marginBottom: 16
            }}>
              Trading Account Created Successfully!
            </h3>
            <p style={{
              color: '#666',
              marginBottom: 24
            }}>
              Your trading account has been created with {formatAmount(parseFloat(amount), currency)}.
              You can now start trading with bots and strategies.
            </p>
            <div style={{
              padding: 16,
              backgroundColor: '#f0f8ff',
              borderRadius: 8,
              border: `1px solid ${branding.primaryColor}20`
            }}>
              <p style={{
                margin: 0,
                fontSize: 14,
                color: branding.textColor
              }}>
                Next steps: Go to Trading Bots to allocate funds to automated trading strategies.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingAccountModal;
